import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

function getHeaders() {
  // Mêmes clés que le service API principal (services/api.ts)
  const token = localStorage.getItem('auth_token');
  let tenantId = '';
  try {
    const tenantData = localStorage.getItem('tenant');
    if (tenantData) {
      tenantId = JSON.parse(tenantData)?.id || '';
    }
  } catch { /* ignore */ }
  return {
    Authorization: `Bearer ${token}`,
    'x-tenant-id': tenantId,
  };
}

const client = axios.create({ baseURL: `${API_URL}/storage` });
client.interceptors.request.use((config) => {
  const headers = getHeaders();
  config.headers.Authorization = headers.Authorization;
  config.headers['x-tenant-id'] = headers['x-tenant-id'];
  return config;
});

// ═══════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════

export interface StorageLocation {
  id: string;
  tenant_id: string;
  aisle: number;
  column: number;
  shelf: number;
  label: string;
  qr_code: string;
  /** Nom libre (« Bac à œufs ») — le code label reste l'identité machine. */
  name: string | null;
  /** Nom de l'allée, joint par le serveur pour l'affichage. */
  aisle_name?: string | null;
  active: boolean;
  notes: string | null;
  created_at: string;
  assignments: StorageAssignment[];
}

/** Une suppression en lot, annulable pendant 30 jours. */
export interface TrashBatch {
  deleted_at: string;
  count: number;
  aisles: number[];
  labels: string[];
  ids: string[];
}

export interface StorageAisle {
  number: number;
  name: string | null;
  notes: string | null;
  location_count: number;
}

export interface StorageAssignment {
  id: string;
  lot_id: string;
  location_id: string;
  quantity: number;
  assigned_at: string;
  lot: {
    id: string;
    lot_number: string;
    expiry_date: string | null;
    stock_backroom: number;
    product: { id: string; name: string; image_url: string | null };
  };
}

export interface PickList {
  id: string;
  pick_number: string;
  type: 'ORDER' | 'REPLENISHMENT';
  status: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  order_id: string | null;
  assigned_to: string | null;
  started_at: string | null;
  completed_at: string | null;
  total_lines: number;
  picked_lines: number;
  created_at: string;
  order?: { id: string; order_number: string; customer_name: string };
  lines?: PickLine[];
}

export interface PickLine {
  id: string;
  pick_list_id: string;
  sequence: number;
  product_id: string;
  product_name: string;
  lot_id: string | null;
  quantity: number;
  location_id: string | null;
  location_label: string | null;
  picked: boolean;
  picked_quantity: number | null;
  picked_at: string | null;
  picked_by: string | null;
  scan_confirmed: boolean;
  order_line_id: string | null;
  location?: {
    id: string;
    label: string;
    aisle: number;
    column: number;
    shelf: number;
  };
}

// ═══════════════════════════════════════════════════════════
// EMPLACEMENTS
// ═══════════════════════════════════════════════════════════

export const storageApi = {
  // Allées (nommage)
  async getAisles(): Promise<StorageAisle[]> {
    const { data } = await client.get('/aisles');
    return data;
  },

  /** Nomme ou renomme une allée. Un nom vide efface le nom. */
  async setAisleName(number: number, dto: { name?: string; notes?: string }) {
    const { data } = await client.patch(`/aisles/${number}`, dto);
    return data;
  },

  // Locations
  async getLocations(filters?: { aisle?: number; active?: boolean }): Promise<StorageLocation[]> {
    const params = new URLSearchParams();
    if (filters?.aisle !== undefined) params.set('aisle', String(filters.aisle));
    if (filters?.active !== undefined) params.set('active', String(filters.active));
    const { data } = await client.get(`/locations?${params}`);
    return data;
  },

  async createLocation(dto: { aisle: number; column: number; shelf: number; name?: string; notes?: string }): Promise<StorageLocation> {
    const { data } = await client.post('/locations', dto);
    return data;
  },

  async createLocationsBatch(dto: {
    aisleFrom: number; aisleTo: number;
    columnFrom: number; columnTo: number;
    shelfFrom: number; shelfTo: number;
    aisleName?: string;
  }): Promise<{ created: number; skipped: number; locations: StorageLocation[] }> {
    const { data } = await client.post('/locations/batch', dto);
    return data;
  },

  async getLocation(id: string): Promise<StorageLocation> {
    const { data } = await client.get(`/locations/${id}`);
    return data;
  },

  async getLocationByQR(qrCode: string): Promise<StorageLocation> {
    const { data } = await client.get(`/locations/scan/${encodeURIComponent(qrCode)}`);
    return data;
  },

  /**
   * Modifie un emplacement, adresse comprise. Déplacer les coordonnées régénère
   * le code sans toucher au QR : `label_changed` porte l'ancien code quand il a
   * changé, pour proposer la réimpression de l'étiquette.
   */
  async updateLocation(
    id: string,
    dto: { aisle?: number; column?: number; shelf?: number; name?: string | null; notes?: string; active?: boolean },
  ): Promise<StorageLocation & { label_changed: string | null }> {
    const { data } = await client.patch(`/locations/${id}`, dto);
    return data;
  },

  async updateLocationsBatch(locationIds: string[], dto: { active?: boolean; notes?: string }): Promise<{ updated: number }> {
    const { data } = await client.patch('/locations/batch', { locationIds, ...dto });
    return data;
  },

  // ── Corbeille ───────────────────────────────────────────
  /** Suppressions des 30 derniers jours, groupées par lot. */
  async getTrash(): Promise<TrashBatch[]> {
    const { data } = await client.get('/trash');
    return data;
  },

  /** Restaure à l'identique : même id, même QR — les étiquettes collées revivent. */
  async restoreFromTrash(ids: string[]): Promise<{ restored: number; restored_labels: string[]; conflicts: string[] }> {
    const { data } = await client.post('/trash/restore', { ids });
    return data;
  },

  async deleteLocationsBatch(locationIds: string[]): Promise<{ deleted: number }> {
    const { data } = await client.delete('/locations/batch', { data: { locationIds } });
    return data;
  },

  async getLocationCount(): Promise<{ count: number }> {
    const { data } = await client.get('/locations/count');
    return data;
  },

  /** Stock du lot pas encore rangé : le maximum réellement rangeable. */
  async getUnassignedStock(lotId: string): Promise<{ unassigned: number }> {
    const { data } = await client.get(`/lots/${lotId}/unassigned`);
    return data;
  },

  /**
   * Intercale une étagère : celles du dessus montent d'un cran et changent de
   * code (leur QR est conservé, donc la marchandise ne bouge pas). `renamed`
   * liste les étiquettes devenues fausses, à réimprimer.
   */
  async insertShelf(dto: { aisle: number; column: number; shelf: number }): Promise<{
    created: StorageLocation;
    renamed: Array<{ id: string; from: string; to: string }>;
  }> {
    const { data } = await client.post('/locations/insert-shelf', dto);
    return data;
  },

  /**
   * Vide un emplacement (retire ce qui y est rangé) SANS toucher au stock :
   * la marchandise repasse simplement en « non rangé ».
   */
  async clearLocation(locationId: string): Promise<{ cleared: number; label: string }> {
    const { data } = await client.post(`/locations/${locationId}/clear`);
    return data;
  },

  // Putaway
  async putaway(dto: { lotId: string; locationQR: string; quantity: number; assignedBy?: string }) {
    const { data } = await client.post('/putaway', dto);
    return data;
  },

  // Pick Lists
  async generatePickList(dto: {
    orderId?: string;
    items?: Array<{ productId: string; quantity: number }>;
    assignedTo?: string;
  }): Promise<PickList> {
    const { data } = await client.post('/pick-lists/generate', dto);
    return data;
  },

  async getPickLists(status?: string): Promise<PickList[]> {
    const params = status ? `?status=${status}` : '';
    const { data } = await client.get(`/pick-lists${params}`);
    return data;
  },

  async getPickList(id: string): Promise<PickList> {
    const { data } = await client.get(`/pick-lists/${id}`);
    return data;
  },

  async startPickList(id: string): Promise<PickList> {
    const { data } = await client.patch(`/pick-lists/${id}/start`);
    return data;
  },

  async pickLine(pickListId: string, lineId: string, dto: {
    scannedQR: string;
    pickedQuantity: number;
    pickedBy?: string;
  }) {
    const { data } = await client.post(`/pick-lists/${pickListId}/lines/${lineId}/pick`, dto);
    return data;
  },

  async completePickList(id: string): Promise<PickList> {
    const { data } = await client.patch(`/pick-lists/${id}/complete`);
    return data;
  },

  async cancelPickList(id: string): Promise<PickList> {
    const { data } = await client.patch(`/pick-lists/${id}/cancel`);
    return data;
  },

  // Utilitaires
  async getProductLocations(productId: string) {
    const { data } = await client.get(`/products/${productId}/locations`);
    return data;
  },
};
